import { useEffect, useLayoutEffect, useRef } from 'react';
import classnames from 'classnames';

import style from '../Input.module.scss';

import { InputLayout, InputLayoutProps } from '../InputLayout';
import { useInput } from '../hooks/useInput';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement>, InputLayoutProps {
    value: string | number;
    dataTest?: string;
    forcePlaceholder?: boolean;
    inputRef?: React.RefObject<HTMLElement | null> | ((el: HTMLElement) => void) | any;
    onChange?(event: React.ChangeEvent<HTMLInputElement>, name?: string): void;
    onBlur?(event: React.FocusEvent<HTMLInputElement>): void;
    onFocus?(event: React.FocusEvent<HTMLInputElement>): void;
}

export function Input({
    value,
    leftTitle,
    title,
    titleHint,
    theme,
    modifiers,
    notValidText,
    iconLinkProps,
    isValid,
    isRequired,
    isImportant,
    showValidStatus,
    dataTest,
    forcePlaceholder,
    placeholder,
    autoComplete,
    onChange,
    onBlur,
    onFocus,
    inputRef: refFromParent,
    ...props
}: InputProps) {
    const { isOnFocus, inputValue, commitValue, handleFocus, handleBlur } = useInput({
        value,
        isControlled: Boolean(onChange),
        name: props.name,
        onBlur,
        onFocus,
    });

    const localRef = useRef<HTMLInputElement>(null);
    const inputRef = (refFromParent ?? localRef) as React.RefObject<HTMLInputElement>;
    const selectionRef = useRef<number | null>(null);

    useLayoutEffect(() => {
        if (inputRef.current && selectionRef.current !== null) {
            if (inputRef.current.selectionStart !== selectionRef.current) {
                inputRef.current.setSelectionRange(selectionRef.current, selectionRef.current);
            }

            selectionRef.current = null;
        }
    }, [inputValue, inputRef]);

    useEffect(() => {
        const node = inputRef.current;

        if (props.type === 'number' && node) {
            const preventScroll = (event: WheelEvent) => {
                const target = event.target as HTMLInputElement;

                if (target.tagName === 'INPUT' && target.type === 'number') {
                    event.preventDefault();
                }
            };

            node.addEventListener('wheel', preventScroll, { passive: false });

            return () => {
                node.removeEventListener('wheel', preventScroll);
            };
        }

        return () => {};
    }, [props.type, inputRef]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        selectionRef.current = event.target.selectionStart;

        if (onChange) {
            onChange(event, props.name);
        }

        commitValue(event.target.value);
    };

    return (
        <InputLayout
            isOnFocus={isOnFocus}
            isEmpty={!inputValue || inputValue === ''}
            disabled={props.disabled}
            leftTitle={leftTitle}
            title={title}
            titleHint={titleHint}
            theme={theme}
            modifiers={modifiers}
            notValidText={notValidText}
            iconLinkProps={iconLinkProps}
            isValid={isValid}
            isRequired={isRequired}
            isImportant={isImportant}
            showValidStatus={showValidStatus}
        >
            <input
                className={classnames([style.input])}
                ref={inputRef}
                data-test={dataTest}
                data-testid={dataTest}
                type={props.type}
                disabled={props.disabled}
                defaultValue={props.defaultValue as any}
                {...props}
                value={inputValue}
                autoComplete={autoComplete || 'off'}
                placeholder={title ? (forcePlaceholder ? placeholder : undefined) : placeholder}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
            />
        </InputLayout>
    );
}
